<template>
  <div class="mb-4">
    <div class="relative">
      <label
        v-if="label !== ''"
        class="block mb-2 text-sm font-medium text-textBlack1 dark:text-textWhite1 transition-colors duration-200"
      >
        {{ label }}
        <span v-if="required" class="text-red-500 ml-1">*</span>
      </label>

      <div class="flex items-center gap-2">
        <div class="relative flex-1">
          <!-- Search Input -->
          <div
            class="relative cursor-pointer"
            @click="toggleDropdown"
            @keydown.enter="toggleDropdown"
            @keydown.escape="closeDropdown"
            tabindex="0"
          >
            <input
              ref="searchInput"
              v-model="searchQuery"
              @input="handleSearch"
              @focus="openDropdown"
              @blur="handleBlur"
              @click="handleClick"
              @mousedown="handleMouseDown"
              :placeholder="displayValue || placeholder"
              :disabled="isDisabled"
              :class="inputClasses"
            />
            
            <!-- Clear button (X) - only show when there's text -->
            <div 
              v-if="searchQuery"
              class="absolute inset-y-0 right-8 flex items-center pr-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-r-lg"
              @click="clearField"
              title="Clear field"
            >
              <i class="fa fa-times text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-sm"></i>
            </div>
            
            <!-- Dropdown arrow -->
            <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <i class="fa fa-chevron-down text-gray-400 transition-transform duration-200" 
                 :class="{ 'rotate-180': isOpen }"></i>
            </div>
          </div>

          <!-- Dropdown Options -->
          <Transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0"
          >
            <div
              v-if="isOpen"
              class="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
            >
              <!-- Search Input in Dropdown -->
              <div class="p-2 border-b border-gray-200 dark:border-gray-600">
                <input
                  ref="dropdownSearchInput"
                  v-model="searchQuery"
                  @input="handleSearch"
                  @keydown.arrow-down="highlightNext"
                  @keydown.arrow-up="highlightPrevious"
                  @keydown.enter="selectHighlighted"
                  @keydown.escape="closeDropdown"
                  :placeholder="`${$t('search')} ${label.toLowerCase()}...`"
                  class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <!-- Options List -->
              <div class="py-1">
                <div
                  v-if="filteredOptions.length === 0"
                  class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400"
                >
                  No options found
                </div>
                <div
                  v-for="(option, index) in filteredOptions"
                  :key="option.id"
                  @click="selectOption(option)"
                  @mouseenter="highlightedIndex = index"
                  :class="[
                    'px-3 py-2 text-sm cursor-pointer transition-colors duration-150',
                    highlightedIndex === index
                      ? 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100'
                      : 'text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
                  ]"
                >
                  {{ option.value }}
                </div>
              </div>
            </div>
          </Transition>
        </div>

        <!-- Add button -->
        <a
          v-if="linkDrop !== ''"
          class="flex-shrink-0 inline-flex items-center justify-center w-10 h-10 text-white bg-primaryColor rounded-lg hover:bg-blue-700 transition-all duration-200 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
          target="_blank"
          :href="linkDrop"
          :title="addButtonTitle"
        >
          <i class="fa fa-plus text-lg"></i>
        </a>
      </div>

      <!-- Error message -->
      <div v-if="isError && errorMsg" class="error-message mt-2 flex items-center text-red-500 text-sm">
        <i class="fa fa-exclamation-circle mr-2"></i>
        {{ errorMsg }}
      </div>
      
      <!-- Helper text -->
      <div v-if="helperText && !isError" class="helper-text mt-2 text-gray-500 dark:text-gray-400 text-sm">
        {{ helperText }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";

const props = defineProps({
  label: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "Select an option",
  },
  modelValue: {
    type: [String, Number],
    default: "",
  },
  isError: {
    type: Boolean,
    default: false,
  },
  errorMsg: {
    type: String,
    default: "",
  },
  items: {
    type: Array,
    default: () => [],
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  linkDrop: {
    type: String,
    default: "",
  },
  call: {
    type: Function,
    default: null,
  },
  required: {
    type: Boolean,
    default: false,
  },
  helperText: {
    type: String,
    default: "",
  },
  size: {
    type: String,
    default: "md",
    validator: (value) => ["sm", "md", "lg"].includes(value),
  },
  addButtonTitle: {
    type: String,
    default: "Add new option",
  },
  searchable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["update:isError", "update:modelValue", "focus", "blur", "change"]);

const isOpen = ref(false);
const searchQuery = ref("");
const highlightedIndex = ref(-1);
const searchInput = ref(null);
const dropdownSearchInput = ref(null);

// Size classes
const sizeClasses = computed(() => {
  const sizes = {
    sm: "px-3 py-2 text-sm",
    md: "px-4 py-2.5 text-sm",
    lg: "px-4 py-3 text-base",
  };
  return sizes[props.size];
});

// Compute input classes based on props and state
const inputClasses = computed(() => {
  const baseClasses = [
    "block w-full rounded-lg transition-all duration-200 ease-in-out",
    "focus:outline-none focus:ring-2 focus:ring-opacity-50",
    "disabled:opacity-50 disabled:cursor-not-allowed",
    "cursor-pointer",
    sizeClasses.value,
    // Ensure space for the left chevron and right clear button
    "pl-10 pr-12",
  ];

  // Error state
  if (props.isError) {
    baseClasses.push(
      "border-red-500 text-red-500 focus:ring-red-500",
      "dark:border-red-600 dark:text-red-500"
    );
  } else {
    // Normal state
    baseClasses.push(
      "bg-white dark:bg-bgFullDark border border-gray-300 dark:border-gray-600",
      "text-textBlack1 dark:text-white",
      "placeholder-gray-400 dark:placeholder-gray-200",
      "focus:ring-blue-500 focus:border-blue-500"
    );
  }

  // Focus state
  if (isOpen.value && !props.isError) {
    baseClasses.push("ring-2 ring-blue-500 ring-opacity-20");
  }

  // Disabled state
  if (props.isDisabled) {
    baseClasses.push("bg-gray-100 dark:bg-gray-800 cursor-not-allowed");
  }

  return baseClasses.join(" ");
});

// Get display value for selected option
const displayValue = computed(() => {
  if (!props.modelValue) return "";
  const selectedOption = props.items.find(item => item.id == props.modelValue);
  return selectedOption ? selectedOption.value : "";
});

// Filter options based on search query
const filteredOptions = computed(() => {
  if (!props.searchable || !searchQuery.value) {
    return props.items;
  }
  
  return props.items.filter(option =>
    option.value.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

// Watch for modelValue changes to update search query
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    const selectedOption = props.items.find(item => item.id == newValue);
    if (selectedOption) {
      searchQuery.value = selectedOption.value;
    }
  } else {
    searchQuery.value = "";
  }
}, { immediate: true });

// Watch for search query changes to open dropdown when typing
watch(() => searchQuery.value, (newQuery) => {
  if (newQuery && !isOpen.value) {
    openDropdown();
  }
});

// Event handlers
const toggleDropdown = () => {
  if (props.isDisabled) return;
  
  if (isOpen.value) {
    closeDropdown();
  } else {
    openDropdown();
  }
};

const openDropdown = () => {
  if (props.isDisabled) return;
  
  isOpen.value = true;
  highlightedIndex.value = -1;
  
  nextTick(() => {
    if (dropdownSearchInput.value) {
      dropdownSearchInput.value.focus();
      dropdownSearchInput.value.select(); // Select all text for easy replacement
    }
  });
  
  emit("focus");
};

const closeDropdown = () => {
  isOpen.value = false;
  highlightedIndex.value = -1;
  
  // Reset search query to display value
  if (props.modelValue) {
    const selectedOption = props.items.find(item => item.id == props.modelValue);
    searchQuery.value = selectedOption ? selectedOption.value : "";
  } else {
    searchQuery.value = "";
  }
  
  emit("blur");
};

const handleMouseDown = (e) => {
  e.preventDefault();
  if (!isOpen.value) {
    openDropdown();
  }
};

const handleClick = (e) => {
  e.preventDefault();
  e.stopPropagation();
  if (!isOpen.value) {
    openDropdown();
  }
};

const handleSearch = () => {
  highlightedIndex.value = -1;
  if (!isOpen.value) {
    openDropdown();
  }
};

const handleBlur = (e) => {
  // Delay closing to allow for option selection
  setTimeout(() => {
    if (!e.relatedTarget || !e.relatedTarget.closest('.relative')) {
      closeDropdown();
    }
  }, 150);
};

// Handle clicks outside the dropdown
const handleClickOutside = (event) => {
  if (isOpen.value && !event.target.closest('.relative')) {
    closeDropdown();
  }
};

const selectOption = (option) => {
  if (!option) return;
  
  // Close dropdown immediately first
  isOpen.value = false;
  highlightedIndex.value = -1;
  
  // Use nextTick to ensure DOM updates
  nextTick(() => {
    emit("update:modelValue", option.id);
    
    // If a callback function is passed, call it
    if (props.call && typeof props.call === "function") {
      props.call(option.id);
    }
    
    emit("change", option);
    
    // Reset search query to display value
    searchQuery.value = option.value;
  });
};

const clearField = () => {
  // Clear the search query
  searchQuery.value = "";
  
  // Close the dropdown
  isOpen.value = false;
  highlightedIndex.value = -1;
  
  // Clear the selected value
  emit("update:modelValue", "");
  emit("change", null);
};

const highlightNext = (e) => {
  e.preventDefault();
  if (highlightedIndex.value < filteredOptions.value.length - 1) {
    highlightedIndex.value++;
  }
};

const highlightPrevious = (e) => {
  e.preventDefault();
  if (highlightedIndex.value > 0) {
    highlightedIndex.value--;
  }
};

const selectHighlighted = (e) => {
  e.preventDefault();
  if (highlightedIndex.value >= 0 && highlightedIndex.value < filteredOptions.value.length) {
    selectOption(filteredOptions.value[highlightedIndex.value]);
  }
};

// Add click outside listener
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
/* Custom focus ring animation */
input:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Error state animation */
input.error {
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
}

/* Error message animation */
.error-message {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Helper text styling */
.helper-text {
  transition: all 0.2s ease-in-out;
}

/* Disabled state styling */
input:disabled {
  background-color: rgba(243, 244, 246, 0.5);
  color: rgba(107, 114, 128, 0.7);
  cursor: not-allowed;
}

.dark input:disabled {
  background-color: rgba(31, 41, 55, 0.5);
  color: rgba(156, 163, 175, 0.7);
}

/* Add button hover effects */
a:hover {
  transform: translateY(-1px);
}

a:active {
  transform: translateY(0);
}

/* Custom scrollbar for dropdown */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.dark .overflow-y-auto::-webkit-scrollbar-track {
  background: #374151;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
  background: #6b7280;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>